import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import type { WizardFormValues } from './schemas';

interface LivePreview2DProps {
  loadTypeLabel?: string | null;
  generating?: boolean;
}

const CANVAS_WIDTH = 480;
const CANVAS_HEIGHT = 320;
const PADDING = 32;

export function LivePreview2D({ loadTypeLabel, generating = false }: LivePreview2DProps) {
  const { control } = useFormContext<WizardFormValues>();
  const [lengthValue, widthValue, heightValue, fireProtection] = useWatch({
    control,
    name: ['length', 'width', 'height', 'fireProtection'],
  });
  const length = Number(lengthValue) || 0;
  const width = Number(widthValue) || 0;
  const height = Number(heightValue) || 0;

  const { rectX, rectY, rectW, rectH, valid } = useMemo(() => {
    if (length <= 0 || width <= 0) {
      return { rectX: 0, rectY: 0, rectW: 0, rectH: 0, valid: false };
    }
    const availW = CANVAS_WIDTH - PADDING * 2;
    const availH = CANVAS_HEIGHT - PADDING * 2;
    const aspect = length / width;
    let w = availW;
    let h = availW / aspect;
    if (h > availH) {
      h = availH;
      w = availH * aspect;
    }
    return {
      rectX: (CANVAS_WIDTH - w) / 2,
      rectY: (CANVAS_HEIGHT - h) / 2,
      rectW: w,
      rectH: h,
      valid: true,
    };
  }, [length, width]);

  return (
    <div className="bg-hilevel-light rounded border border-hilevel-border p-4 mx-auto w-full lg:max-w-none">
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-xs uppercase tracking-wide text-hilevel-gray font-semibold">
          {generating ? 'Generating budget' : 'Live preview'}
        </span>
        {valid && (
          <span className="text-xs text-hilevel-gray">
            {length} m × {width} m
          </span>
        )}
      </div>
      <div className="relative">
        <svg
          viewBox={`0 0 ${CANVAS_WIDTH} ${CANVAS_HEIGHT}`}
          className={`w-full h-auto bg-white border border-hilevel-border rounded transition-opacity ${
            generating ? 'opacity-30' : 'opacity-100'
          }`}
          preserveAspectRatio="xMidYMid meet"
          aria-label="2D preview of the floor"
          aria-busy={generating}
        >
          {valid ? (
            <>
              <rect
                x={rectX}
                y={rectY}
                width={rectW}
                height={rectH}
                fill="#fff8f9"
                stroke="#db1e36"
                strokeWidth={2}
              />
              {/* dimension labels */}
              <text
                x={rectX + rectW / 2}
                y={rectY - 8}
                textAnchor="middle"
                fontSize={12}
                fill="#343e48"
              >
                {length} m
              </text>
              <text
                x={rectX - 8}
                y={rectY + rectH / 2}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize={12}
                fill="#343e48"
              >
                {width} m
              </text>
            </>
          ) : (
            <text
              x={CANVAS_WIDTH / 2}
              y={CANVAS_HEIGHT / 2}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={14}
              fill="#78787a"
            >
              Enter length and width to see a preview
            </text>
          )}
        </svg>
        {generating && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded bg-white/70 backdrop-blur-sm"
            role="status"
            aria-live="polite"
            data-testid="preview-generating-overlay"
          >
            <FontAwesomeIcon
              icon={faSpinner}
              className="text-2xl text-hilevel-red animate-spin"
              aria-hidden="true"
            />
            <span className="text-sm font-semibold text-hilevel-slate">
              Calculating your layout…
            </span>
            <span className="text-xs text-hilevel-gray">This usually takes a few seconds</span>
          </div>
        )}
      </div>
      {valid && (
        <div className="flex flex-wrap gap-2 mt-3">
          {loadTypeLabel && (
            <span className="inline-flex items-center bg-white border border-hilevel-border rounded-full px-3 py-1 text-xs text-hilevel-slate">
              {loadTypeLabel}
            </span>
          )}
          {height > 0 && (
            <span className="inline-flex items-center bg-white border border-hilevel-border rounded-full px-3 py-1 text-xs text-hilevel-slate">
              {height} m tall
            </span>
          )}
          {fireProtection && (
            <span className="inline-flex items-center bg-hilevel-red text-white rounded-full px-3 py-1 text-xs font-semibold">
              Fire-rated
            </span>
          )}
        </div>
      )}
    </div>
  );
}
